import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Link } from "react-router-dom";
import { GraduationCap, BookOpen, Award, Users, Clock, FileCheck2 } from "lucide-react";

export default function About() {
  return (
    <div className="min-h-screen">
      <Navbar/>
      <section className="max-w-5xl mx-auto px-6 lg:px-8 py-20">
        <div className="text-xs uppercase tracking-[0.3em] gold-text mb-4">Our approach</div>
        <h1 className="font-serif text-5xl md:text-6xl tracking-tighter-x brand-navy mb-8">
          A classical method,<br/>delivered through modern tools.
        </h1>
        <p className="text-xl text-slate-600 leading-relaxed max-w-3xl">
          Genius Education is led by <strong className="brand-navy">Aman</strong> — the sole tutor, running small, focused GCSE Maths classes out of Wokingham. Our mission is simple: give every student the knowledge, confidence and exam-technique they need to excel at GCSE, in a classroom where they're actually <em>known</em>.
        </p>
      </section>

      <section className="bg-white border-y border-slate-200">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 py-20 grid md:grid-cols-3 gap-10">
          {[
            {icon:<GraduationCap/>, t:"One tutor, five years, hundreds of students", d:"Every lesson is taught by Aman — an experienced GCSE Maths specialist who has been teaching exam-level maths for the past five years."},
            {icon:<BookOpen/>, t:"Carefully curated material", d:"We hand-pick the best worksheets, past-paper drills and video explanations from across the industry, so your child learns from whatever explains it best — not whoever wrote it."},
            {icon:<Award/>, t:"Measurable outcomes", d:"Each parent receives a clear written report after every assessment. The average grade improvement across our cohort is +2 grades in nine months."}
          ].map((x,i)=>(
            <div key={i}>
              <div className="w-12 h-12 rounded-md bg-brand-navy flex items-center justify-center mb-5" style={{color:"#D4AF37"}}>{x.icon}</div>
              <h3 className="font-serif text-2xl brand-navy mb-3">{x.t}</h3>
              <p className="text-slate-600 leading-relaxed">{x.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Class structure */}
      <section className="max-w-5xl mx-auto px-6 lg:px-8 py-20">
        <h2 className="font-serif text-4xl tracking-tighter-x brand-navy mb-3">How the classes run</h2>
        <p className="text-slate-600 mb-12 max-w-2xl">Small groups, generous time, and real focus on the student.</p>
        <div className="grid md:grid-cols-3 gap-6">
          <StructCard icon={<Users size={22}/>} t="Maximum 8 students per batch" d="Small enough to know every student. Large enough for healthy discussion and a little friendly competition."/>
          <StructCard icon={<Clock size={22}/>} t="90–120 minute sessions" d="Each class runs 90 to 120 minutes, depending on the topic of the day. Complex topics get the time they deserve."/>
          <StructCard icon={<FileCheck2 size={22}/>} t="In-person or online" d="Attend at our Wokingham branch, or join live online — both formats get the same teaching and the same homework."/>
        </div>
      </section>

      <section className="bg-white border-y border-slate-200">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 py-20">
          <h2 className="font-serif text-4xl tracking-tighter-x brand-navy mb-12">The weekly rhythm</h2>
          <div className="space-y-8">
            {[
              ["01","Live weekly class","A 90–120 minute session for up to 8 students of similar ability. Topic taught, worked examples dissected, questions solved together."],
              ["02","Targeted practice from the vault","Homework hand-selected from our 20,000+ question bank — only the problems your child actually needs."],
              ["03","AI tutor clinic","Stuck after school? Our Claude-powered AI tutor walks through step-by-step workings, 24/7. We review chat logs to spot patterns."],
              ["04","Error-book review","Every wrong answer goes into a private error book. If something needs urgent attention, we'll organise a mid-week error-book session; otherwise we address it together in the next live class."]
            ].map(([n,t,d])=>(
              <div key={n} className="grid grid-cols-12 gap-6 border-t border-slate-200 pt-8">
                <div className="col-span-2 font-serif text-4xl gold-text">{n}</div>
                <div className="col-span-10">
                  <h3 className="font-serif text-2xl brand-navy mb-2">{t}</h3>
                  <p className="text-slate-600 leading-relaxed">{d}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-20 panel-navy rounded-xl p-12 text-center">
            <h3 className="font-serif text-3xl md:text-4xl mb-4">See the method in action.</h3>
            <p className="text-white/70 mb-8">Book a free trial class at our Wokingham branch — in person or online.</p>
            <Link to="/signup" className="btn-gold px-8 py-3 rounded-md inline-block" data-testid="about-cta">Start free trial</Link>
          </div>
        </div>
      </section>

      <Footer/>
    </div>
  );
}

function StructCard({icon, t, d}) {
  return (
    <div className="lift-card p-6 rounded-lg bg-white border border-slate-200">
      <div className="w-10 h-10 rounded-md bg-brand-navy flex items-center justify-center mb-4" style={{color:"#D4AF37"}}>{icon}</div>
      <h4 className="font-serif text-xl brand-navy mb-2">{t}</h4>
      <p className="text-sm text-slate-600 leading-relaxed">{d}</p>
    </div>
  );
}
